import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import type { Row } from "./api";
import { useAdmin } from "./context";
import { CustomerDetail } from "./CustomerDetail";
import { day, isTest, planStatus } from "./format";
import { Empty, LoadError, PageHead, StatusChip, TestChip, useServerNow, WaitingChip } from "./parts";
import { inboxRows, queueQuery } from "./QueueTab";

const QKEY = "precision_admin.q";
const readQ = () => {
  try {
    return sessionStorage.getItem(QKEY) || "";
  } catch {
    return "";
  }
};

/** Customers: search, or one customer when the URL carries ?customer=<id> (queue and staff-change rows link here). */
export function CustomersTab() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const id = typeof search.customer === "string" ? search.customer : null;
  return id ? <CustomerDetail id={id} /> : <CustomerSearch />;
}

function CustomerSearch() {
  const { key, api } = useAdmin();
  const [text, setText] = useState(readQ);
  const [q, setQ] = useState(() => readQ().trim());
  useEffect(() => {
    const t = setTimeout(() => {
      const v = text.trim();
      setQ(v);
      try {
        sessionStorage.setItem(QKEY, v);
      } catch {
        /* a private window keeps no search; nothing breaks */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [text]);

  const res = useQuery({
    queryKey: [key, "customers", q],
    queryFn: () => api.get<Row[]>(`customers?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });
  // Damien 30 Oct: a blank tab reads as broken, so with no search it lists the 50 most recent customers. An API
  // without the list answers an empty one (the search prompt then shows); a failed call shows the error.
  const recent = useQuery({
    queryKey: [key, "customers", "recent"],
    queryFn: () => api.get<Row[]>("customers?recent=1"),
    enabled: q.length < 2,
  });
  // Until the database lists recent customers (admin_recent_customers, Zach), the blank tab lists the customers the
  // inbox already holds: everyone waiting on a reply, most urgent first (Damien 31 Oct: never blank until you search).
  const inbox = useQuery({ ...queueQuery(key, api), enabled: q.length < 2 && !!recent.data && !recent.data.length });
  const waiting = inboxCustomers(inbox.data);
  const now = useServerNow(null, 0);

  return (
    <div>
      <PageHead title="Customers" sub="Find a customer and read what they have been through." />
      <Card>
        <CardContent className="space-y-3">
          <Input
            autoFocus
            data-nonmutating
            placeholder="Search by email or name (2+ letters)"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {q.length < 2 ? (
            recent.isLoading ? (
              <Empty>Loading…</Empty>
            ) : recent.error ? (
              <Alert role="alert">
                <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    Couldn’t load recent customers ({recent.error instanceof Error ? recent.error.message : String(recent.error)}).
                    Search by email or name above, or try again.
                  </span>
                  <Button size="sm" variant="secondary" data-nonmutating onClick={() => void recent.refetch()}>Try again</Button>
                </AlertDescription>
              </Alert>
            ) : recent.data?.length ? (
              <>
                <p className="text-sm font-semibold">Recent customers</p>
                <CustomerRows rows={recent.data} now={now} />
              </>
            ) : inbox.isLoading ? (
              <Empty>Loading…</Empty>
            ) : inbox.error ? (
              <LoadError what="load the customers waiting on a reply" error={inbox.error} />
            ) : waiting.length ? (
              <>
                <p className="text-sm font-semibold">Customers waiting on a reply</p>
                <WaitingRows rows={waiting} now={now} />
                <p className="text-sm text-ks-grey-1">Search by email or name above to find anyone else.</p>
              </>
            ) : (
              <Empty>Search by email or name.</Empty>
            )
          ) : res.isLoading ? (
            <Empty>Searching…</Empty>
          ) : res.error ? (
            <LoadError what="search" error={res.error} />
          ) : res.data?.length ? (
            <>
              <CustomerRows rows={res.data} now={now} />
              <p className="text-sm text-ks-grey-1">No match? Try their name instead of their email.</p>
            </>
          ) : (
            <Empty>No customer matches “{q}”. Try their name, or the email they used in Intercom.</Empty>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** One row per customer: the search results and the recent list share it. */
function CustomerRows({ rows, now }: { rows: Row[]; now: number }) {
  const { openCustomer, openQueue } = useAdmin();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Customer</TableHead>
          <TableHead>Product</TableHead>
          <TableHead>Goal</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Last check-in</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((c) => (
          <TableRow key={c.id} className="cursor-pointer" onClick={() => openCustomer(c.id)}>
            <TableCell className="whitespace-normal">
              <div className="font-semibold">{c.name || c.email}{isTest(c) ? <> <TestChip /></> : null}</div>
              {c.name ? <div className="text-xs text-ks-grey-1">{c.email}</div> : null}
            </TableCell>
            <TableCell>{c.product || "—"}</TableCell>
            <TableCell>{c.goal_level_2 || c.goal || "—"}</TableCell>
            <TableCell className="max-w-[18rem] whitespace-normal">
              <span className="flex flex-wrap items-center gap-1">
                <StatusChip status={planStatus(c)} />
                {isTest(c) ? null : <WaitingChip w={c.waiting} now={now} onOpen={openQueue} />}
              </span>
            </TableCell>
            <TableCell>{day(c.last_checkin_at)}{c.last_checkin_day != null ? ` · Day ${c.last_checkin_day}` : ""}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** One row per customer in the inbox (their most urgent open item), in the inbox's order, test accounts last. */
export function inboxCustomers(d: Row | undefined): Row[] {
  const seen = new Set<string>();
  const out: Row[] = [];
  for (const [, x] of inboxRows(d)) {
    const k = String(x.user_id || x.email || "");
    if (!x.user_id || seen.has(k) || x.handled_here) continue;
    seen.add(k);
    out.push({ id: x.user_id, name: x.name, email: x.email, goal: x.goal, is_test: x.is_test, waiting: x });
  }
  return out;
}

/** The inbox's customers: the queue reads no plan or check-in, so this list shows only what it has. */
function WaitingRows({ rows, now }: { rows: Row[]; now: number }) {
  const { openCustomer, openQueue } = useAdmin();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Customer</TableHead>
          <TableHead>Goal</TableHead>
          <TableHead>Waiting</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((c) => (
          <TableRow key={c.id} className="cursor-pointer" onClick={() => openCustomer(c.id)}>
            <TableCell className="whitespace-normal">
              <div className="font-semibold">{c.name || c.email}{isTest(c) ? <> <TestChip /></> : null}</div>
              {c.name ? <div className="text-xs text-ks-grey-1">{c.email}</div> : null}
            </TableCell>
            <TableCell className="whitespace-normal">{c.goal || "—"}</TableCell>
            <TableCell className="max-w-[18rem] whitespace-normal">
              {isTest(c) ? <span className="text-ks-grey-1">Test: no deadline</span> : <WaitingChip w={c.waiting} now={now} onOpen={openQueue} />}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
